import type { KeyboardEventHandler, MouseEventHandler, PointerEventHandler } from 'react'

/**
 * MENU CLIC DROIT — les outils purs du socle (lot 2, 24/10/2026).
 *
 * Rien ici ne touche React (types seulement), le routeur ni le document :
 * ces calculs se testent sans navigateur (tests/menu-contextuel-socle.test.ts).
 * Les pages les importent depuis `menu-contextuel.tsx`, qui les réexporte.
 */

/* ─── composer avec les gestionnaires déjà posés par la page ─── */

/**
 * Les gestionnaires que le menu pose sur l'élément qui le porte. Une page en
 * a parfois déjà sur ce même élément : Entrée qui ouvre une ligne
 * `tr role="button"` (FinanceRevenuePage.tsx:169, ValidationWorkstation.tsx:637),
 * glisser d'une tuile ou d'une section (LayoutGrid.tsx:225, SectionOrder.tsx:141).
 * En JSX le dernier prop gagne : étaler le menu par-dessus les écrasait,
 * l'étaler dessous privait le menu de la touche Menu et de l'appui long.
 */
export interface GestionnairesHote {
  onContextMenu?: MouseEventHandler<HTMLElement> | undefined
  onKeyDown?: KeyboardEventHandler<HTMLElement> | undefined
  onPointerDown?: PointerEventHandler<HTMLElement> | undefined
  onPointerUp?: PointerEventHandler<HTMLElement> | undefined
  onPointerMove?: PointerEventHandler<HTMLElement> | undefined
  onPointerCancel?: PointerEventHandler<HTMLElement> | undefined
  onClickCapture?: MouseEventHandler<HTMLElement> | undefined
}

/** Ce que rend `useMenuContextuel()(…)` — ou `{}` quand le menu est inerte. */
export type DeclencheursComposes = GestionnairesHote & { 'data-menu-contextuel'?: '' }

type Evenement = { readonly defaultPrevented: boolean }
type Appel<E> = ((e: E) => void) | undefined

/** La page d'abord ; le menu seulement si personne n'a pris l'événement. */
function pageDAbord<E extends Evenement>(page: Appel<E>, menu: Appel<E>): Appel<E> {
  if (!page || !menu) return page ?? menu
  return (e) => {
    page(e)
    if (!e.defaultPrevented) menu(e)
  }
}

/** Les deux, toujours, la page d'abord : le menu n'y fait qu'arrêter son minuteur. */
function lesDeux<E>(page: Appel<E>, menu: Appel<E>): Appel<E> {
  if (!page || !menu) return page ?? menu
  return (e) => { page(e); menu(e) }
}

/**
 * Fusionne les déclencheurs du menu avec les gestionnaires que la page avait
 * déjà sur l'élément. Chacun voit le même événement, donc le `preventDefault`
 * de l'autre :
 *
 *   · clic droit, clavier : la page d'abord. Si elle a pris l'événement
 *     (son propre menu, sa propre touche), le menu s'efface ;
 *   · appui : la page d'abord. Si elle a pris le geste (un glisser commence),
 *     l'appui long du menu est aussitôt annulé — jamais un menu au milieu
 *     d'un déplacement ;
 *   · déplacement, lever, annulation : les deux, toujours ;
 *   · clic (capture) : le menu d'abord. S'il mange le clic du lever du doigt
 *     après un appui long, la page ne le voit pas : il suivrait le lien ou
 *     ouvrirait la ligne sous le menu.
 *
 *     <tr role="button" tabIndex={0} onClick={ouvrirNuit}
 *       {...composerDeclencheurs(menu(() => actionsNuit(n), titre), { onKeyDown: entreeOuvre })}>
 *
 * Menu inerte (préférence coupée, page hors shell) : les gestionnaires de la
 * page, tels quels.
 */
export function composerDeclencheurs(declencheurs: DeclencheursComposes, existants: GestionnairesHote): DeclencheursComposes {
  const m = declencheurs
  const p = existants
  const r: DeclencheursComposes = { ...p, ...m }
  const poser = <K extends keyof GestionnairesHote>(k: K, v: GestionnairesHote[K]) => {
    if (v) r[k] = v
    else delete r[k]
  }
  poser('onContextMenu', pageDAbord(p.onContextMenu, m.onContextMenu))
  poser('onKeyDown', pageDAbord(p.onKeyDown, m.onKeyDown))
  poser('onPointerMove', lesDeux(p.onPointerMove, m.onPointerMove))
  poser('onPointerUp', lesDeux(p.onPointerUp, m.onPointerUp))
  poser('onPointerCancel', lesDeux(p.onPointerCancel, m.onPointerCancel))

  const appuiPage = p.onPointerDown
  const appuiMenu = m.onPointerDown
  const annulerAppui = m.onPointerCancel
  poser('onPointerDown', !appuiPage || !appuiMenu ? appuiPage ?? appuiMenu : (e) => {
    appuiPage(e)
    // Toujours appelé : il remet à zéro la garde du clic d'après l'appui long.
    appuiMenu(e)
    if (e.defaultPrevented) annulerAppui?.(e)
  })

  const clicPage = p.onClickCapture
  const clicMenu = m.onClickCapture
  poser('onClickCapture', !clicPage || !clicMenu ? clicPage ?? clicMenu : (e) => {
    const avant = e.defaultPrevented
    clicMenu(e)
    if (!avant && e.defaultPrevented) return
    clicPage(e)
  })
  return r
}

/* ─── la page affichée ─── */

/** L'adresse de la page affichée — `window.location` dans le navigateur. */
export interface AdresseAffichee { origin: string; pathname: string; search: string }

const lieuActuel = (): AdresseAffichee | undefined => (typeof window === 'undefined' ? undefined : window.location)

/** Une recherche comparable : `?b=2&a=1` et `?a=1&b=2` désignent la même liste. */
function rechercheTriee(search: string): string {
  const p = new URLSearchParams(search)
  p.sort()
  return p.toString()
}

/**
 * Le lien mène-t-il à la page déjà affichée ? Le routeur ne remonte pas une
 * page montée (router.tsx:97-110) : y naviguer ne fait rien (même chemin,
 * même recherche, router.tsx:103), ou change l'adresse sans changer l'écran
 * quand la page ne lit son adresse qu'au montage. Le menu masque alors
 * l'entrée plutôt que d'offrir un clic qui ne mène nulle part.
 *
 * Compare le chemin, et la recherche quand le lien en porte une (dans
 * n'importe quel ordre). L'ancre ne compte pas : elle ne change pas de page.
 * `ignorerRecherche` : pour un écran qui ne lit son adresse qu'au montage
 * (AccessHistoryPage, ValidationWorkstation…), le même chemin avec une autre
 * recherche ne changerait que l'adresse — c'est encore la page courante.
 *
 * À appeler dans le constructeur du menu (à l'ouverture), jamais au rendu.
 */
export function estPageCourante(href: string, opts: { ignorerRecherche?: boolean | undefined; ici?: AdresseAffichee | undefined } = {}): boolean {
  const ici = opts.ici ?? lieuActuel()
  if (!ici) return false
  let cible: URL
  try { cible = new URL(href, `${ici.origin}${ici.pathname}${ici.search}`) } catch { return false }
  if (cible.origin !== ici.origin || cible.pathname !== ici.pathname) return false
  if (opts.ignorerRecherche || cible.search === '') return true
  return rechercheTriee(cible.search) === rechercheTriee(ici.search)
}

/** Le lien absolu d'un chemin de Gestion, collable dans un message. */
export function lienAbsolu(href: string, ici: AdresseAffichee | undefined = lieuActuel()): string {
  if (!ici) return href
  try { return new URL(href, `${ici.origin}${ici.pathname}${ici.search}`).href } catch { return href }
}
